import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api.js";
import { useOptionalAuth } from "../auth.js";
import {
  ActionConfirmation,
  type ActionOutcome,
} from "../ui/ActionConfirmation.js";
import { idempotencyKey } from "./api.js";
import { BulkCancellationDecisions } from "./BulkCancellationDecisions.js";
import {
  cancellationChoicesComplete,
  cancellationCreditChoices,
  chooseAllCancellationCredits,
  keepValidCreditChoices,
  type CancellationChoices,
} from "./cancellationChoices.js";
import { NotifyToggle } from "./notify.js";
import {
  staffBooking,
  type BulkCancelPreview,
  type BulkCancelResult,
} from "./staff.js";
import { scheduleBlockOutcome } from "./scheduleBlockOutcome.js";
import {
  cancellationPreviewChanged,
  confirmsCancellationPreview,
  confirmsCancellationResult,
  confirmsTimeOff,
  conflictDescription,
  normalizeTimeOffReason,
  timeOffCancellationOutcome,
  type TimeOffCancellationOperation,
} from "./timeOffCancellation.js";
import {
  scheduleBlocks,
  type ScheduleBlockApply,
  type ScheduleBlockInput,
  type ScheduleBlockPreview,
  type StaffScheduleBlock,
} from "./scheduleBlocks.js";

export interface ScheduleBlockFormState {
  blockDate: string;
  endDate: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
  reason: string;
}

export const EMPTY_SCHEDULE_BLOCK_FORM: ScheduleBlockFormState = {
  blockDate: "",
  endDate: "",
  allDay: true,
  startTime: "09:00",
  endTime: "17:00",
  reason: "Unavailable",
};

export function scheduleBlockFormFor(
  block: StaffScheduleBlock,
): ScheduleBlockFormState {
  return {
    blockDate: block.block_date,
    endDate: block.end_date,
    allDay: block.start_time === null,
    startTime: block.start_time?.slice(0, 5) ?? "09:00",
    endTime: block.end_time?.slice(0, 5) ?? "17:00",
    reason: block.reason,
  };
}

export function scheduleBlockFormFromDiary(
  day: string,
  startMinute: number,
): ScheduleBlockFormState {
  const lastMinute = 24 * 60 - 1;
  // Keep at least a quarter-hour of block even when the click lands near midnight,
  // so the finish never clamps back onto (or before) the start.
  const safeStart = Math.max(0, Math.min(lastMinute - 15, startMinute));
  const safeEnd = Math.min(lastMinute, safeStart + 60);
  return {
    blockDate: day,
    endDate: day,
    allDay: false,
    startTime: clockTime(safeStart),
    endTime: clockTime(safeEnd),
    reason: "Unavailable",
  };
}

function clockTime(minute: number): string {
  const hour = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function toInput(form: ScheduleBlockFormState): ScheduleBlockInput {
  return {
    block_date: form.blockDate,
    end_date: form.allDay ? form.endDate || form.blockDate : form.blockDate,
    start_time: form.allDay ? null : form.startTime,
    end_time: form.allDay ? null : form.endTime,
    reason: normalizeTimeOffReason(form.reason),
  };
}

export function ScheduleBlockSafetyNotice() {
  return (
    <div className="notice">
      <strong>Blocking time prevents new bookings.</strong> Existing lessons
      stay booked unless you explicitly select cancellation after previewing.
    </div>
  );
}

function readPending(
  key: string,
  providerId: number,
): TimeOffCancellationOperation | null {
  const saved = JSON.parse(
    sessionStorage.getItem(key) ?? "null",
  ) as TimeOffCancellationOperation | null;
  if (saved === null) return null;
  if (
    typeof saved.key !== "string" ||
    !saved.key ||
    saved.applied?.block.provider_id !== providerId ||
    !Array.isArray(saved.appointmentIds) ||
    saved.appointmentIds.length === 0 ||
    saved.appointmentIds.length > 100 ||
    saved.appointmentIds.some((id) => !Number.isSafeInteger(id) || id <= 0) ||
    new Set(saved.appointmentIds).size !== saved.appointmentIds.length
  ) {
    throw new Error("The saved cancellation could not be checked.");
  }
  return saved;
}

export function ScheduleBlockForm({
  providerId,
  initial,
  blockId = null,
  onSaved,
  onCancel,
  onBusyChange,
  onStart,
}: {
  providerId: number;
  initial: ScheduleBlockFormState;
  blockId?: string | null;
  onSaved: (applied: ScheduleBlockApply, outcome?: ActionOutcome) => void;
  onCancel?: () => void;
  onBusyChange?: (busy: boolean) => void;
  onStart?: () => void;
}) {
  const auth = useOptionalAuth();
  const storageKey = `wmf:time-off-cancellation:${auth?.me?.user?.id ?? "staff"}:${providerId}`;
  const [recovery] = useState(() => {
    try {
      return {
        operation: readPending(storageKey, providerId),
        unavailable: false,
      };
    } catch {
      return { operation: null, unavailable: true };
    }
  });
  const restored = recovery.operation;
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState<ScheduleBlockPreview | null>(null);
  const [busyAction, setBusyAction] = useState<
    "preview" | "cancellation-preview" | "save" | "check" | "resume" | null
  >(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [alsoCancel, setAlsoCancel] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(false);
  const [cancellationPreview, setCancellationPreview] =
    useState<BulkCancelPreview | null>(null);
  const [eligibility, setEligibility] = useState<BulkCancelPreview["items"]>(
    [],
  );
  const [choices, setChoices] = useState<CancellationChoices>({});
  const [savedBlock, setSavedBlock] = useState<ScheduleBlockApply | null>(null);
  const [operation, setOperation] =
    useState<TimeOffCancellationOperation | null>(restored);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(
    restored
      ? {
          variant: "uncertain",
          title: "Cancellation outcome needs checking",
          details: [
            {
              value:
                "An existing cancellation was interrupted. Check its outcome before another cancellation. You can still block time without cancelling lessons.",
            },
            { label: "Operation identity", value: restored.key },
          ],
        }
      : null,
  );
  const [checkedOperation, setCheckedOperation] = useState<{
    result: BulkCancelResult;
    applied: ScheduleBlockApply;
  } | null>(null);
  const [canResume, setCanResume] = useState(false);
  const working = useRef(false);
  const cancellationKey = useRef(restored?.key ?? idempotencyKey());
  const busy = busyAction !== null;
  const blockLocked = busy || savedBlock !== null;
  const cancellationLocked = busy || operation !== null;
  const wantsCancellation = alsoCancel && selectedIds.length > 0;
  const reasonValid = reason.trim().length <= 500;
  const batchTooLarge =
    (preview?.conflict_count ?? 0) > 100 ||
    (preview?.conflicting_appointments.length ?? 0) > 100;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  function change(patch: Partial<ScheduleBlockFormState>) {
    if (blockLocked) return;
    setForm((current) => ({ ...current, ...patch }));
    setPreview(null);
    setCancellationPreview(null);
    setEligibility([]);
    setSelectedIds([]);
    setAlsoCancel(false);
    setChoices({});
    setProblem(null);
    setOutcome(null);
  }

  async function previewBlock() {
    if (working.current || blockLocked) return;
    if (!form.blockDate || !form.reason.trim()) {
      setProblem("Choose the date and enter a reason.");
      return;
    }
    if (
      !form.allDay &&
      (!form.startTime || !form.endTime || form.startTime >= form.endTime)
    ) {
      setProblem("The finish time must be after the start time.");
      return;
    }
    onStart?.();
    working.current = true;
    setBusyAction("preview");
    setProblem(null);
    try {
      setPreview(await scheduleBlocks.preview(providerId, toInput(form)));
      setAlsoCancel(false);
      setSelectedIds([]);
      setCancellationPreview(null);
      setEligibility([]);
      setChoices({});
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "We could not preview that block.",
      );
    } finally {
      working.current = false;
      setBusyAction(null);
    }
  }

  async function loadCancellationPreview(
    ids: number[],
    cancellationReason: string,
  ) {
    let next = await staffBooking.bulkCancelPreview(
      ids,
      cancellationReason.trim(),
    );
    if (!confirmsCancellationPreview(next, ids))
      throw new Error(
        "The cancellation preview did not confirm every selected lesson. Check again before saving.",
      );
    const assessedItems = next.items;
    setEligibility((current) => [
      ...new Map(
        [...current, ...assessedItems].map((item) => [
          item.appointment_id,
          item,
        ]),
      ).values(),
    ]);
    // Only the cancellation backend can declare a conflict cancellable. Keep
    // rejected rows visible, and preview the exact reduced batch before saving.
    const cancellableIds = next.items
      .filter((item) => item.can_cancel)
      .map((item) => item.appointment_id);
    setSelectedIds(cancellableIds);
    if (cancellableIds.length === 0) {
      setCancellationPreview(null);
      setChoices({});
      return;
    }
    if (cancellableIds.length !== ids.length) {
      next = await staffBooking.bulkCancelPreview(
        cancellableIds,
        cancellationReason.trim(),
      );
      if (
        !confirmsCancellationPreview(next, cancellableIds) ||
        next.items.some((item) => !item.can_cancel)
      ) {
        throw new Error(
          "The lessons changed during checking. Review cancellation again before saving.",
        );
      }
    }
    setCancellationPreview(next);
    setChoices((current) => keepValidCreditChoices(next, current));
  }

  async function reviewCancellation(
    ids = selectedIds,
    cancellationReason = reason,
  ) {
    if (
      working.current ||
      operation !== null ||
      batchTooLarge ||
      ids.length === 0
    )
      return;
    if (cancellationReason.trim().length > 500) {
      setProblem("The cancellation note must be 500 characters or fewer.");
      return;
    }
    const effectiveReason = cancellationReason.trim() || form.reason;
    working.current = true;
    setBusyAction("cancellation-preview");
    setProblem(null);
    setCancellationPreview(null);
    try {
      await loadCancellationPreview(ids, effectiveReason);
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "We could not preview the cancellations.",
      );
    } finally {
      working.current = false;
      setBusyAction(null);
    }
  }

  function toggleCancellation(next: boolean) {
    if (cancellationLocked || batchTooLarge || recovery.unavailable) return;
    setAlsoCancel(next);
    setCancellationPreview(null);
    setChoices({});
    const ids = next
      ? [
          ...new Set(
            preview?.conflicting_appointments.map(
              (item) => item.appointment_id,
            ) ?? [],
          ),
        ]
      : [];
    setSelectedIds(ids);
    if (next) {
      const prefilledReason = form.reason.trim();
      setReason(prefilledReason);
      void reviewCancellation(ids, prefilledReason);
    }
  }

  function finish(
    applied: ScheduleBlockApply,
    next?: ActionOutcome,
    settled = false,
  ) {
    // Saving unrelated time off must never discard an unresolved cancellation.
    if (settled || (!operation && !recovery.unavailable))
      sessionStorage.removeItem(storageKey);
    if (settled) {
      setOperation(null);
      setCheckedOperation(null);
      setCanResume(false);
      cancellationKey.current = idempotencyKey();
    }
    setSavedBlock(null);
    setPreview(null);
    setCancellationPreview(null);
    setAlsoCancel(false);
    setSelectedIds([]);
    setChoices({});
    setOutcome(null);
    setNotify(false);
    if (blockId === null) setForm(initial);
    onSaved(applied, next);
  }

  function resetBlockDraft() {
    setSavedBlock(null);
    setForm(initial);
    setPreview(null);
    setCancellationPreview(null);
    setEligibility([]);
    setAlsoCancel(false);
    setSelectedIds([]);
    setChoices({});
    setNotify(false);
  }

  function cancellationReceipt(
    current: TimeOffCancellationOperation,
    result: BulkCancelResult,
    applied: ScheduleBlockApply,
  ) {
    const next = timeOffCancellationOutcome({ ...current, applied }, result);
    const active = confirmsTimeOff(
      applied,
      providerId,
      toInput(scheduleBlockFormFor(current.applied.block)),
    );
    return active
      ? next
      : {
          ...next,
          variant: "attention" as const,
          title: "Cancellation checked; time-off block needs review",
          details: [
            {
              value:
                "The cancellation operation is final. The original time-off block is no longer confirmed active; check the schedule before relying on it.",
            },
            ...(next.details ?? []).slice(1),
          ],
        };
  }

  function settleCancellation(
    current: TimeOffCancellationOperation,
    result: BulkCancelResult,
    applied = current.applied,
  ) {
    const next = timeOffCancellationOutcome({ ...current, applied }, result);
    if (next.variant === "uncertain") {
      setOutcome(next);
      resetBlockDraft();
      return;
    }
    const cancelledIds = new Set(
      result.results
        .filter((row) => row.success)
        .map((row) => row.appointment_id),
    );
    const remaining = applied.schedule.conflicting_appointments.filter(
      (row) => !cancelledIds.has(row.appointment_id),
    );
    finish(
      {
        ...applied,
        schedule: {
          ...applied.schedule,
          conflicting_appointments: remaining,
          conflict_count: remaining.length,
        },
      },
      cancellationReceipt(current, result, applied),
      true,
    );
  }

  async function runCancellation(
    current: TimeOffCancellationOperation,
    checking = false,
  ) {
    try {
      const result = await staffBooking.bulkCancel(
        current.appointmentIds,
        current.reason,
        current.key,
        current.notify,
        current.creditResolutions,
      );
      settleCancellation(current, result);
    } catch (cause) {
      if (!checking && cancellationPreviewChanged(cause)) {
        // The initial policy refusal releases its reservation before any write.
        sessionStorage.removeItem(storageKey);
        setOperation(null);
        setCancellationPreview(null);
        setOutcome({
          variant: "attention",
          title: "Time blocked; cancellation preview changed",
          details: [
            {
              value:
                "No lessons were cancelled by this attempt. Review the refreshed credit/payment choices and confirm again.",
            },
          ],
        });
        try {
          await loadCancellationPreview(current.appointmentIds, current.reason);
        } catch (previewError) {
          setProblem(
            previewError instanceof Error
              ? previewError.message
              : "The cancellations could not be checked.",
          );
        }
      } else {
        const policyRefused = checking && cancellationPreviewChanged(cause);
        setOutcome({
          variant: policyRefused ? "attention" : "uncertain",
          title: policyRefused
            ? "Existing cancellation needs review"
            : "Cancellation outcome needs checking",
          details: [
            {
              value: policyRefused
                ? "The saved cancellation choices were refused. This does not settle earlier cancellations. The original operation is retained for checking; you can still block other time without cancelling lessons."
                : "Some lessons may still be booked. Check the original operation before another cancellation attempt. You can still block other time without cancelling lessons.",
            },
            {
              value:
                cause instanceof Error
                  ? cause.message
                  : "The cancellation response could not be confirmed.",
            },
            { label: "Operation identity", value: current.key },
          ],
        });
        setCanResume(false);
        resetBlockDraft();
      }
    }
  }

  async function applyBlock() {
    if (
      working.current ||
      preview === null ||
      (wantsCancellation &&
        (operation !== null ||
          batchTooLarge ||
          !reasonValid ||
          !cancellationPreview ||
          !confirmsCancellationPreview(cancellationPreview, selectedIds) ||
          !cancellationChoicesComplete(cancellationPreview, choices)))
    )
      return;
    onStart?.();
    working.current = true;
    setBusyAction("save");
    setProblem(null);
    setOutcome(null);
    let applied = savedBlock;
    try {
      if (applied === null) {
        applied =
          blockId === null
            ? await scheduleBlocks.create(providerId, toInput(form))
            : await scheduleBlocks.update(providerId, blockId, toInput(form));
        if (!confirmsTimeOff(applied, providerId, toInput(form))) {
          finish(applied, {
            ...scheduleBlockOutcome(applied.block, "Time off saved"),
            variant: "uncertain",
            title: "Blocked time needs checking",
            details: [
              {
                value:
                  "The returned block did not confirm the requested time off. Zero cancellation writes were made. Check Needs review before trying again.",
              },
            ],
          });
          return;
        }
        setSavedBlock(applied);
        if (wantsCancellation) {
          const currentConflicts =
            applied.schedule.conflicting_appointments.map(
              (item) => item.appointment_id,
            );
          const originalConflicts = preview.conflicting_appointments.map(
            (item) => item.appointment_id,
          );
          if (
            currentConflicts.length !== originalConflicts.length ||
            currentConflicts.some((id) => !originalConflicts.includes(id))
          ) {
            setPreview(applied.schedule);
            setSelectedIds((current) =>
              current.filter((id) => currentConflicts.includes(id)),
            );
            setCancellationPreview(null);
            setOutcome({
              variant: "attention",
              title: "Time blocked; overlapping lessons changed",
              details: [
                {
                  value:
                    "Zero cancellation writes were made. Review the current overlapping lessons and confirm again.",
                },
              ],
            });
            return;
          }
        }
      }
      if (!wantsCancellation || !cancellationPreview) {
        finish(applied);
        return;
      }
      const current: TimeOffCancellationOperation = {
        key: cancellationKey.current,
        appointmentIds: [...selectedIds],
        reason: reason.trim() || form.reason,
        notify,
        creditResolutions: cancellationCreditChoices(
          cancellationPreview,
          choices,
        ),
        preview: cancellationPreview,
        choices: { ...choices },
        applied,
      };
      // Persist the original request before its first destructive write, so a
      // closed diary drawer or page reload cannot create a fresh identity.
      sessionStorage.setItem(storageKey, JSON.stringify(current));
      setOperation(current);
      await runCancellation(current);
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "We could not save that block.",
      );
      if (applied !== null)
        setOutcome({
          variant: "attention",
          title: "Time blocked; no cancellation attempted",
          details: [
            {
              value:
                "Time off saved, but the cancellation could not be started. Existing lessons remain booked.",
            },
          ],
        });
    } finally {
      working.current = false;
      setBusyAction(null);
    }
  }

  async function readOriginalBlock(current: TimeOffCancellationOperation) {
    const active = (await scheduleBlocks.list(providerId)).find(
      (block) => block.id === current.applied.block.id,
    );
    return {
      ...current.applied,
      block: active ?? {
        ...current.applied.block,
        status: "requires_attention",
      },
    };
  }

  async function checkExistingOperation() {
    if (working.current || operation === null) return;
    working.current = true;
    setBusyAction("check");
    setProblem(null);
    setCanResume(false);
    setCheckedOperation(null);
    try {
      // This GET cannot execute cancellation, resend emails, or renew a lease.
      let checked: Awaited<ReturnType<typeof staffBooking.bulkCancelOperation>>;
      try {
        checked = await staffBooking.bulkCancelOperation(operation.key);
      } catch (cause) {
        // Only this API's own "no receipt for this actor and key" answer counts.
        // A generic 404 (an API without the lookup) stays unconfirmed.
        if (
          !(
            cause instanceof ApiError &&
            cause.status === 404 &&
            cause.message === "Operation not found"
          )
        )
          throw cause;
        const original = await readOriginalBlock(operation);
        const stillActive = confirmsTimeOff(
          original,
          providerId,
          toInput(scheduleBlockFormFor(operation.applied.block)),
        );
        setCanResume(stillActive);
        setProblem(
          stillActive
            ? "The server has no record of this cancellation request, so it cancelled nothing. You can explicitly resume the original request with the same identity, or block other time without cancellation."
            : "The server has no record of this cancellation request and the time-off block is no longer confirmed active. Cancellation will not be resumed. You can block other time while keeping this operation for checking.",
        );
        return;
      }
      if (
        checked.idempotency_key !== operation.key ||
        !["pending", "completed"].includes(checked.state) ||
        (checked.state === "pending" && checked.result !== null)
      )
        throw new Error("The operation response could not be confirmed.");
      if (checked.state === "completed") {
        if (
          !checked.result ||
          !confirmsCancellationResult(checked.result, operation.appointmentIds)
        )
          throw new Error(
            "The cancellation response did not confirm every selected lesson.",
          );
        let applied: ScheduleBlockApply;
        try {
          applied = await readOriginalBlock(operation);
        } catch {
          applied = {
            ...operation.applied,
            block: { ...operation.applied.block, status: "requires_attention" },
          };
        }
        setCheckedOperation({ result: checked.result, applied });
        setOutcome(cancellationReceipt(operation, checked.result, applied));
        return;
      }
      const applied = await readOriginalBlock(operation);
      const active = confirmsTimeOff(
        applied,
        providerId,
        toInput(scheduleBlockFormFor(operation.applied.block)),
      );
      setCanResume(active);
      setProblem(
        active
          ? "The cancellation operation has no final receipt yet. You can explicitly resume its original request, or block other time without cancellation."
          : "The time-off block is no longer confirmed active. Cancellation will not be resumed. You can block other time while keeping this operation for checking.",
      );
    } catch (cause) {
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The existing cancellation could not be checked.",
      );
    } finally {
      working.current = false;
      setBusyAction(null);
    }
  }

  async function resumeExistingOperation() {
    if (working.current || !operation || !canResume) return;
    working.current = true;
    setBusyAction("resume");
    setProblem(null);
    try {
      // Re-check the block immediately before this explicit, same-key write.
      const applied = await readOriginalBlock(operation);
      if (
        !confirmsTimeOff(
          applied,
          providerId,
          toInput(scheduleBlockFormFor(operation.applied.block)),
        )
      )
        throw new Error(
          "The time-off block is no longer confirmed active. Zero cancellation writes were made.",
        );
      const checked = await staffBooking.bulkCancelPreview(
        operation.appointmentIds,
        operation.reason,
      );
      if (!confirmsCancellationPreview(checked, operation.appointmentIds))
        throw new Error(
          "The authoritative appointment state could not be checked.",
        );
      await runCancellation({ ...operation, applied }, true);
    } catch (cause) {
      setCanResume(false);
      setProblem(
        cause instanceof Error
          ? cause.message
          : "The cancellation could not be resumed.",
      );
    } finally {
      working.current = false;
      setBusyAction(null);
    }
  }

  const excluded =
    preview?.conflicting_appointments.filter(
      (item) => !selectedIds.includes(item.appointment_id),
    ) ?? [];
  const blocked = eligibility.filter((item) => !item.can_cancel);
  return (
    <div className="schedule-block-form">
      {recovery.unavailable && (
        <div className="error" role="alert">
          Cancellation recovery is unavailable. You can block time, but
          cancellation remains disabled until you can check the saved operation.
          Reload the page or review the affected lessons in the Action Centre.
        </div>
      )}
      {outcome && <ActionConfirmation {...outcome} reveal />}
      {problem && (
        <div className="error" role="alert">
          {problem}
        </div>
      )}
      <fieldset
        className="panel"
        disabled={blockLocked}
        style={{ minWidth: 0 }}
      >
        <div className="row" style={{ alignItems: "end", flexWrap: "wrap" }}>
          <label>
            From date
            <input
              type="date"
              value={form.blockDate}
              onChange={(event) => change({ blockDate: event.target.value })}
            />
          </label>
          {form.allDay && (
            <label>
              To date
              <input
                type="date"
                min={form.blockDate || undefined}
                value={form.endDate}
                onChange={(event) => change({ endDate: event.target.value })}
              />
            </label>
          )}
          <label className="row small" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={form.allDay}
              onChange={(event) => change({ allDay: event.target.checked })}
            />
            All day
          </label>
        </div>
        {!form.allDay && (
          <div
            className="row"
            style={{ alignItems: "end", flexWrap: "wrap", marginTop: 12 }}
          >
            <label>
              From
              <input
                type="time"
                value={form.startTime}
                onChange={(event) => change({ startTime: event.target.value })}
              />
            </label>
            <label>
              To
              <input
                type="time"
                value={form.endTime}
                onChange={(event) => change({ endTime: event.target.value })}
              />
            </label>
            <span className="small muted">
              Partial-day blocks are for one date only.
            </span>
          </div>
        )}
        <label className="stack" style={{ marginTop: 12 }}>
          Reason
          <input
            type="text"
            maxLength={120}
            value={form.reason}
            onChange={(event) => change({ reason: event.target.value })}
            placeholder="Holiday"
          />
        </label>
        <div className="row" style={{ marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={blockLocked}
            onClick={() => void previewBlock()}
          >
            {busyAction === "preview" ? "Checking…" : "Preview"}
          </button>
        </div>
      </fieldset>
      {onCancel && (
        <button
          className="quiet"
          type="button"
          disabled={busy}
          onClick={onCancel}
        >
          {operation ? "Close; keep cancellation for checking" : "Cancel edit"}
        </button>
      )}
      {operation && (
        <section className="panel" aria-label="Existing cancellation">
          <p>
            The original cancellation is kept for checking. You can block time
            below; new cancellation stays disabled until the existing operation
            is final.
          </p>
          <p>
            Original time off: {operation.applied.block.block_date} to{" "}
            {operation.applied.block.end_date} ·{" "}
            {operation.applied.block.reason}
          </p>
          <p>
            {operation.appointmentIds.length} lessons · Cancellation reason:{" "}
            {operation.reason} · Cancellation emails:{" "}
            {operation.notify ? "On" : "Off"}
          </p>
          <ul>
            {operation.applied.schedule.conflicting_appointments
              .filter((lesson) =>
                operation.appointmentIds.includes(lesson.appointment_id),
              )
              .map((lesson) => (
                <li key={lesson.appointment_id}>
                  {conflictDescription(lesson)}
                </li>
              ))}
          </ul>
          <BulkCancellationDecisions
            preview={operation.preview}
            busy
            choices={operation.choices}
            onChoice={() => {}}
            onChooseAll={() => {}}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => void checkExistingOperation()}
          >
            {busyAction === "check"
              ? "Checking existing cancellation…"
              : "Check existing cancellation"}
          </button>
          {checkedOperation && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                settleCancellation(
                  operation,
                  checkedOperation.result,
                  checkedOperation.applied,
                )
              }
            >
              Finish checked cancellation
            </button>
          )}
          {canResume && (
            <button
              className="action-danger"
              type="button"
              disabled={busy}
              onClick={() => void resumeExistingOperation()}
            >
              {busyAction === "resume"
                ? "Resuming cancellation…"
                : "Resume original cancellation"}
            </button>
          )}
        </section>
      )}
      {preview && (
        <div className="panel" style={{ minWidth: 0 }}>
          <h3>{savedBlock ? "Time already blocked" : "Preview"}</h3>
          <p>
            <strong>{preview.reason}</strong> ·{" "}
            {preview.block_date === preview.end_date
              ? preview.block_date
              : `${preview.block_date} to ${preview.end_date}`}{" "}
            ·{" "}
            {preview.all_day
              ? "all day"
              : `${preview.start_time?.slice(0, 5)}–${preview.end_time?.slice(0, 5)}`}
          </p>
          {preview.warning && <div className="notice">{preview.warning}</div>}
          {preview.conflict_count > 0 ? (
            <>
              <p className="small">
                {preview.conflict_count} existing lesson
                {preview.conflict_count === 1 ? "" : "s"} overlap.{" "}
                {!alsoCancel && "They will remain booked."}
              </p>
              {batchTooLarge ? (
                <div className="notice">
                  More than 100 lessons overlap. Narrow the time-off range or
                  cancel in smaller reviewed batches. This workflow cannot
                  cancel the whole set at once.
                </div>
              ) : (
                <label
                  className="row"
                  style={{ flexWrap: "nowrap", alignItems: "start" }}
                >
                  <input
                    type="checkbox"
                    checked={alsoCancel}
                    disabled={cancellationLocked || recovery.unavailable}
                    onChange={(event) =>
                      toggleCancellation(event.target.checked)
                    }
                  />
                  <span>Also cancel lessons during this time off</span>
                </label>
              )}
              <div className="stack" style={{ marginTop: 12 }}>
                {preview.conflicting_appointments.map((conflict) => {
                  const decision = eligibility.find(
                    (item) => item.appointment_id === conflict.appointment_id,
                  );
                  return (
                    <label
                      key={conflict.appointment_id}
                      className="row"
                      style={{ alignItems: "start", flexWrap: "nowrap" }}
                    >
                      {alsoCancel && (
                        <input
                          type="checkbox"
                          aria-label={`Cancel lesson #${conflict.appointment_id}`}
                          checked={selectedIds.includes(
                            conflict.appointment_id,
                          )}
                          disabled={
                            cancellationLocked || decision?.can_cancel === false
                          }
                          onChange={(event) => {
                            setSelectedIds((current) =>
                              event.target.checked
                                ? [...current, conflict.appointment_id]
                                : current.filter(
                                    (id) => id !== conflict.appointment_id,
                                  ),
                            );
                            setCancellationPreview(null);
                          }}
                        />
                      )}
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {conflictDescription(conflict)}
                        {decision?.can_cancel === false && (
                          <span className="small muted">
                            {" "}
                            ·{" "}
                            {decision.message ||
                              decision.current_status ||
                              "Cannot currently be cancelled"}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="small muted">
              No existing lessons overlap this block.
            </p>
          )}
          {alsoCancel && !batchTooLarge && (
            <div className="stack" style={{ marginTop: 16 }}>
              <strong>
                {selectedIds.length} of {preview.conflict_count} lessons
                selected for cancellation
              </strong>
              <label className="stack">
                Note for the cancelled lessons (optional)
                <input
                  type="text"
                  value={reason}
                  maxLength={500}
                  disabled={cancellationLocked}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setCancellationPreview(null);
                  }}
                />
              </label>
              {!reasonValid && (
                <p className="small muted">
                  The cancellation note must be 500 characters or fewer.
                </p>
              )}
              <NotifyToggle
                checked={notify}
                onChange={setNotify}
                disabled={cancellationLocked}
                warn
                label="Email the student about each cancelled lesson"
              />
              <button
                type="button"
                disabled={
                  cancellationLocked || !reasonValid || selectedIds.length === 0
                }
                onClick={() => void reviewCancellation()}
              >
                {busyAction === "cancellation-preview"
                  ? "Checking cancellations…"
                  : "Review cancellation"}
              </button>
            </div>
          )}
          <section
            aria-label="Final confirmation"
            className="stack"
            style={{ marginTop: 16 }}
          >
            <h3>
              Confirm time off{wantsCancellation ? " and cancellations" : ""}
            </h3>
            <p>
              {preview.block_date} to {preview.end_date} ·{" "}
              {preview.all_day
                ? "all day"
                : `${preview.start_time?.slice(0, 5)}–${preview.end_time?.slice(0, 5)}`}{" "}
              · Block reason: {preview.reason}
            </p>
            <p>
              {preview.conflict_count} overlapping lessons ·{" "}
              {alsoCancel ? selectedIds.length : 0} selected for cancellation ·
              Cancellation emails: {alsoCancel && notify ? "On" : "Off"}
            </p>
            {alsoCancel && (
              <>
                {reason.trim() && <p>Cancellation note: {reason.trim()}</p>}
                {excluded.length > 0 && (
                  <div>
                    <strong>{excluded.length} excluded</strong>
                    <ul>
                      {excluded.map((item) => (
                        <li key={item.appointment_id}>
                          {conflictDescription(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {blocked.length > 0 && (
                  <div>
                    <strong>Cannot currently be cancelled</strong>
                    <ul>
                      {blocked.map((item) => (
                        <li key={item.appointment_id}>
                          Lesson #{item.appointment_id} —{" "}
                          {item.message ||
                            item.current_status ||
                            "Not cancellable"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
            {cancellationPreview && alsoCancel && (
              <BulkCancellationDecisions
                preview={cancellationPreview}
                busy={cancellationLocked}
                choices={choices}
                onChoice={(id, choice) =>
                  setChoices((current) => ({ ...current, [id]: choice }))
                }
                onChooseAll={(choice) =>
                  setChoices((current) =>
                    chooseAllCancellationCredits(
                      cancellationPreview,
                      current,
                      choice,
                    ),
                  )
                }
              />
            )}
            {alsoCancel && (
              <p className="small muted">
                Recurring plans continue after the time off. School timetable
                lessons are managed separately.
              </p>
            )}
            <button
              className={wantsCancellation ? "action-danger" : undefined}
              type="button"
              disabled={
                busy ||
                (wantsCancellation &&
                  (batchTooLarge ||
                    !reasonValid ||
                    !cancellationPreview ||
                    !cancellationChoicesComplete(cancellationPreview, choices)))
              }
              onClick={() => void applyBlock()}
            >
              {busyAction === "save"
                ? "Saving…"
                : wantsCancellation
                  ? savedBlock
                    ? `Cancel ${selectedIds.length} ${selectedIds.length === 1 ? "lesson" : "lessons"} (time already blocked)`
                    : `Block time and cancel ${selectedIds.length} ${selectedIds.length === 1 ? "lesson" : "lessons"}`
                  : "Block time"}
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
